import * as Phoenix from 'phoenix';
import find from 'lodash/find';
import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';

import { store } from './../store';
import { logInfo, logError } from './services';
import { serializeFileBasic } from './serializers/serializeFile';
import BaseSubscription from './BaseSubscription';


export const TYPING_USERS_CHANGE = 'TYPING_USERS_CHANGE';
export const TYPING_USERS_SUBSCRIBE_SUCCESS = 'TYPING_USERS_SUBSCRIBE_SUCCESS';
export const TYPING_USERS_SUBSCRIBE_ERROR = 'TYPING_USERS_SUBSCRIBE_ERROR';


export default class TypingUsersSubscription extends BaseSubscription {

  phoenixSocket = null;
  channel = null;
  typedText = '';
  currentlyTypingUsers = [];
  isSubscribedToRoom = false;

  connect = () => {
    const socketUrl = process.env.ADMIN_SOCKET_URL;
    const authToken = store.getState().authToken;

    this.phoenixSocket = new Phoenix.Socket(socketUrl, {
      params: {
        token: authToken
      }
    });

    return new Promise((resolve, reject) => {
      this.phoenixSocket.onError(error => {
        const errorMessage = 'TypingUsersSubscription: Failed to subscribe - cannot open connection via Phoenix.Socket';
        logError(errorMessage, { error });
        this.triggerEvent(TYPING_USERS_SUBSCRIBE_ERROR, { error });
        reject(error);
      });
      this.phoenixSocket.onOpen(() => {
        resolve();
      });
      this.phoenixSocket.connect();
    });
  };

  subscribeToRoom = async (roomId, callback = () => {}) => {
    if (this.isSubscribedToRoom) {
      await this.unsubscribeFromCurrentRoom();
    }
    this.channel = this.phoenixSocket.channel('admin:room:' + roomId, {});
    this.channel.join()
      .receive('error', error => {
        logError('TypingUsersSubscription: Failed to subscribe due to error', { error });
        this.triggerEvent(TYPING_USERS_SUBSCRIBE_ERROR, { error });
      })
      .receive('timeout', error => {
        logError('TypingUsersSubscription: Failed to subscribe due to timeout', { error });
        this.triggerEvent(TYPING_USERS_SUBSCRIBE_ERROR, { error });
      })
      .receive('ok', (data) => {
        this.roomId = roomId;
        this.isSubscribedToRoom = true;
        this.subscribeStatusChange();
        this.dispatchTypedText(this.typedText, true);
        this.triggerEvent(TYPING_USERS_SUBSCRIBE_SUCCESS, data);
        callback(data);
      })
  };

  subscribeStatusChange = () => {
    this.channel.on('presence_state', presence => {
      this.onStatusChange(presence, true);
    });
    this.channel.on('presence_diff', presence => {
      if (presence.joins) {
        this.onStatusChange(presence.joins);
      }
    });
  };

  onStatusChange(presenceState, hideTypingLabel = false) {
    const presenceStateUserValues = Object.values(presenceState);
    presenceStateUserValues.forEach(value => {
      const userId = value.id;
      if (userId === store.getState().currentOperator.id) {
        return;
      }
      const metas = value.metas?.[0] || {};
      const userIsCurrentlyTyping = metas.typing;
      const userData = {
        id: userId,
        avatar: serializeFileBasic({
          url: value?.avatar,
        }),
        firstName: value.first_name || '',
        lastName: value.last_name || '',
        isOperator: value.type === 'employee',
        typedText: metas.text || '',
        isTyping: hideTypingLabel ? false : userIsCurrentlyTyping,
      };
      const hasUserAlreadyBeenTyping = find(this.currentlyTypingUsers, { id: userId });
      if (userIsCurrentlyTyping) {
        if (hasUserAlreadyBeenTyping) {
          this.changeCurrentlyTypingUser(userId, userData);
        }
        else {
          this.addCurrentlyTypingUser(userData);
        }
        if (!hideTypingLabel) {
          this.debouncedRemoveTypingLabel(userId);
        }
      }
      else {
        if (hasUserAlreadyBeenTyping) {
          this.removeCurrentlyTypingUser(userId);
        }
      }
    });
  }

  changeCurrentlyTypingUser = (userId, userDataDiff) => {
    this.onTypingUsersChange(
      cloneDeep(this.currentlyTypingUsers).map(user => {
        return user.id === userId
          ? { ...user, ...userDataDiff }
          : user;
      })
    );
  };

  removeCurrentlyTypingUser = (userId) => {
    this.onTypingUsersChange(
      this.currentlyTypingUsers.filter(user => user.id !== userId)
    );
  };

  addCurrentlyTypingUser = (userData) => {
    this.onTypingUsersChange([...this.currentlyTypingUsers, userData]);
  };

  onTypingUsersChange = (currentlyTypingUsers) => {
    this.currentlyTypingUsers = currentlyTypingUsers;
    this.triggerEvent(TYPING_USERS_CHANGE, currentlyTypingUsers);
  };

  debouncedRemoveTypingLabel = debounce((userId) => {
    this.changeCurrentlyTypingUser(userId, { isTyping: false });
  }, 2000);

  dispatchTypedText = (typedText, dispatchForcefully = false) => {
    if (this.channel) {
      const text = typeof typedText === 'string' ? typedText.trim() : '';
      const typing = Boolean(text);

      if (this.typedText !== text || dispatchForcefully) {
        this.typedText = text;
        this.channel.push('typing', { typing, text });
      }
    }
  };

  unsubscribeFromCurrentRoom = () => {
    logInfo('TypingUsersSubscription: Unsubscribing...');

    this.isSubscribedToRoom = false;
    this.roomId = null;
    this.typedText = '';
    this.currentlyTypingUsers = [];

    if (this.channel) {
      this.channel.leave();
    }
  };

  destroy = (callback) => {
    this.phoenixSocket.disconnect(callback);
  };
}
